import React from 'react';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import { TextFormField } from 'lib/form/TextFormField';

export type LoginTab = 'phone' | 'email';

interface IdentitySelectorProps {
    useSeparateInputs: boolean;
    identity: string;
    activeTab: LoginTab;
    onTabChange: (tab: LoginTab) => void;
    countryPicker: React.ReactNode;
    showCountryInfo: boolean;
}

export const IdentitySelector: React.FC<IdentitySelectorProps> = ({
    useSeparateInputs,
    identity,
    activeTab,
    onTabChange,
    countryPicker,
    showCountryInfo
}) => {
    const { t } = useTranslation();

    const handleChange = React.useCallback(
        (_: React.MouseEvent<HTMLElement>, newTab: LoginTab | null) => {
            if (newTab) {
                onTabChange(newTab);
            }
        },
        [onTabChange]
    );

    if (!useSeparateInputs) {
        return (
            <Grid size={12}>
                <TextFormField
                    name="identity"
                    label={t(`ONBOARDING_SIGNUP_LABEL_${identity.toUpperCase()}`)}
                    clearable
                    clearAriaLabel="Clear"
                    InputProps={{
                        autoComplete: 'email',
                        ...(showCountryInfo && { startAdornment: countryPicker })
                    }}
                />
            </Grid>
        );
    }

    return (
        <>
            <Grid size={12}>
                <ToggleButtonGroup
                    value={activeTab}
                    exclusive
                    onChange={handleChange}
                    fullWidth
                    sx={{
                        borderRadius: 999,
                        bgcolor: 'action.hover',
                        p: 0.5,
                        '& .MuiToggleButton-root': {
                            borderRadius: 999,
                            border: 'none',
                            textTransform: 'none',
                            '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': {
                                    bgcolor: 'primary.main'
                                }
                            }
                        }
                    }}
                >
                    <ToggleButton value="phone">{t('ONBOARDING_SIGNUP_LABEL_PHONE')}</ToggleButton>
                    <ToggleButton value="email">{t('ONBOARDING_SIGNUP_LABEL_EMAIL')}</ToggleButton>
                </ToggleButtonGroup>
            </Grid>

            <Grid size={12}>
                {activeTab === 'email' ? (
                    <TextFormField
                        name="email"
                        type="email"
                        label={t('ONBOARDING_SIGNUP_LABEL_EMAIL')}
                        clearable
                        clearAriaLabel="Clear email field"
                        InputProps={{ autoComplete: 'email' }}
                    />
                ) : (
                    <TextFormField
                        name="phone"
                        type="tel"
                        label={t('ONBOARDING_SIGNUP_LABEL_PHONE')}
                        clearable
                        clearAriaLabel="Clear phone field"
                        InputProps={{ startAdornment: countryPicker }}
                    />
                )}
            </Grid>
        </>
    );
};
